// Property Detail Page — the core sales rep flow (BUILD_SPEC §4.1).
// Three stacked panels:
//   A. Property card (name, badge, city, regional + mgmt co links, contact)
//   B. Regional book — every property under the same regional, with Apollo
//      rows tinted green (the sister-property pitch view)
//   C. Management Company stats — 4 KPIs + link to company detail
//
// Below the regional book, a reserved <div className="directory-notes-slot" />
// for the Phase 2 Notes panel (per-property CRM).

import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import DirectoryTopNav from '../components/DirectoryTopNav';
import ApolloBadge from '../components/ApolloBadge';
import PropertyRow from '../components/PropertyRow';

export default function DirectoryPropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function setApollo(value) {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await api.setDirectoryApollo(id, value);
      setData(updated);
    } catch (err) {
      alert(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  async function clearOverride() {
    if (saving) return;
    if (!window.confirm('Clear the manual override? The property will revert to the matcher\u2019s flag from the last refresh.')) return;
    setSaving(true);
    try {
      const updated = await api.clearDirectoryApolloOverride(id);
      setData(updated);
    } catch (err) {
      alert(err.message || 'Failed to clear override');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.getDirectoryProperty(id)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err?.responseBody?.error || err.message || 'Failed to load property'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Sort the regional book: current property first, then alphabetical.
  const sortedProperties = useMemo(() => {
    if (!data?.regional?.properties) return [];
    const props = data.regional.properties.slice();
    props.sort((a, b) => {
      if (a.is_current && !b.is_current) return -1;
      if (b.is_current && !a.is_current) return 1;
      return a.name.localeCompare(b.name);
    });
    return props;
  }, [data]);

  if (loading) {
    return (
      <div className="directory-page">
        <DirectoryTopNav />
        <div className="loading">Loading property...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="directory-page">
        <DirectoryTopNav />
        <div className="card" style={{ padding: 24, marginTop: 16 }}>
          <h2>Property not found</h2>
          <p style={{ color: '#475569' }}>{error || 'Try searching again from the Directory.'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/sales/directory')}>Back to search</button>
        </div>
      </div>
    );
  }

  const { property, regional, company } = data;
  const regionalContactName = (regional.first_name || regional.last_name)
    ? `${regional.first_name || ''} ${regional.last_name || ''}`.trim()
    : '';

  return (
    <div className="directory-page">
      <DirectoryTopNav />

      <button
        className="directory-back"
        onClick={() => navigate('/sales/directory')}
        type="button"
      >
        &larr; Back to search
      </button>

      {/* ── A. Property card ──────────────────────────────────────── */}
      <section className="card directory-property-card">
        <div className="directory-property-card__head">
          <h1 className="directory-property-card__name">{property.name}</h1>
          <ApolloBadge isCustomer={property.apollo_customer} />
        </div>

        {isSuperAdmin ? (
          <div className="directory-apollo-toggle">
            <span className="directory-apollo-toggle__label">Admin override:</span>
            {property.apollo_customer ? (
              <button
                type="button"
                className="btn btn-sm directory-apollo-toggle__btn"
                onClick={() => setApollo(false)}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Mark as NOT an Apollo customer'}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm directory-apollo-toggle__btn directory-apollo-toggle__btn--green"
                onClick={() => setApollo(true)}
                disabled={saving}
              >
                {saving ? 'Saving...' : '\u2713 Mark as Apollo customer'}
              </button>
            )}
            {data.override ? (
              <button
                type="button"
                className="directory-apollo-toggle__clear"
                onClick={clearOverride}
                disabled={saving}
                title={`Set ${data.override.set_at} by user ${data.override.set_by_user_id}`}
              >
                Clear override
              </button>
            ) : null}
            {data.override ? (
              <span className="directory-apollo-toggle__hint">
                Manually overridden {new Date(data.override.set_at.replace(' ', 'T') + 'Z').toLocaleDateString()}
              </span>
            ) : null}
          </div>
        ) : null}

        <dl className="directory-property-card__meta">
          <div>
            <dt>City</dt>
            <dd>{property.city || '—'}</dd>
          </div>
          <div>
            <dt>Management Company</dt>
            <dd>
              <Link to={`/sales/directory/companies/${encodeURIComponent(company.id)}`}>{company.name}</Link>
            </dd>
          </div>
          <div>
            <dt>Regional Supervisor</dt>
            <dd>
              {regional.is_unassigned ? (
                <span style={{ color: '#64748b' }}>Unassigned</span>
              ) : (
                <>{regional.name}{regionalContactName && regionalContactName !== regional.name ? ` (${regionalContactName})` : ''}</>
              )}
            </dd>
          </div>
          {regional.email ? (
            <div>
              <dt>Regional Email</dt>
              <dd><a href={`mailto:${regional.email}`}>{regional.email}</a></dd>
            </div>
          ) : null}
          {regional.phone ? (
            <div>
              <dt>Regional Phone</dt>
              <dd><a href={`tel:${regional.phone.replace(/[^0-9+]/g, '')}`}>{regional.phone}</a></dd>
            </div>
          ) : null}
        </dl>
      </section>

      {/* ── B. Regional book ──────────────────────────────────────── */}
      <section className="directory-section">
        <div className="directory-section__header">
          <h2 className="directory-section__title">
            Regional: {regional.is_unassigned ? <em>Unassigned</em> : regional.name}
          </h2>
          <p className="directory-section__stat">
            <strong>{regional.property_count}</strong> properties &middot;{' '}
            <strong>{regional.apollo_customer_count}</strong> Apollo customers &middot;{' '}
            <strong>{regional.coverage_pct}%</strong> coverage
          </p>
        </div>

        <div className="directory-regional-book">
          {sortedProperties.length === 0 ? (
            <p style={{ color: '#64748b', padding: 12 }}>No sibling properties.</p>
          ) : (
            sortedProperties.map(p => (
              <PropertyRow key={p.id} property={p} current={p.is_current} />
            ))
          )}
        </div>
      </section>

      {/* Phase 2 hook — Notes panel will live here. Empty in v1. */}
      <div className="directory-notes-slot" data-property-id={property.id} />

      {/* ── C. Management Company stats ───────────────────────────── */}
      <section className="directory-section">
        <div className="directory-section__header">
          <h2 className="directory-section__title">Management Company: {company.name}</h2>
        </div>
        <div className="directory-kpi-row">
          <Kpi label="Total Props" value={company.property_count} />
          <Kpi label="Apollo" value={company.apollo_customer_count} accent="green" />
          <Kpi label="Opportunities" value={(company.property_count || 0) - (company.apollo_customer_count || 0)} />
          <Kpi label="Coverage" value={`${company.coverage_pct}%`} />
        </div>
        <Link className="directory-link" to={`/sales/directory/companies/${encodeURIComponent(company.id)}`}>
          View full company &rarr;
        </Link>
      </section>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={'directory-kpi' + (accent === 'green' ? ' directory-kpi--green' : '')}>
      <div className="directory-kpi__value">{value}</div>
      <div className="directory-kpi__label">{label}</div>
    </div>
  );
}
